import React, { useEffect, useState } from "react";
import { getImpactStats, updateImpactStats } from "../lib/api";
import { toast } from "sonner";
import useNoIndex from "../hooks/useNoIndex";

const formatKg = (n) =>
  new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 }).format(n || 0);

const AdminImpact = () => {
  useNoIndex();
  const [token, setToken] = useState(
    () => sessionStorage.getItem("mise-admin-token") || "",
  );
  const [authed, setAuthed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kg_rescued: 0,
    meals_enabled: 0,
    co2e_avoided_kg: 0,
  });

  useEffect(() => {
    getImpactStats()
      .then((data) =>
        setForm({
          kg_rescued: Number(data.kg_rescued || 0),
          meals_enabled: Number(data.meals_enabled || 0),
          co2e_avoided_kg: Number(data.co2e_avoided_kg || 0),
        }),
      )
      .catch(() => {});
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error("Enter the admin token");
      return;
    }
    sessionStorage.setItem("mise-admin-token", token.trim());
    setAuthed(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await updateImpactStats(
        {
          kg_rescued: Number(form.kg_rescued),
          meals_enabled: Number(form.meals_enabled),
          co2e_avoided_kg: Number(form.co2e_avoided_kg),
        },
        token.trim(),
      );
      setForm({
        kg_rescued: Number(saved.kg_rescued || 0),
        meals_enabled: Number(saved.meals_enabled || 0),
        co2e_avoided_kg: Number(saved.co2e_avoided_kg || 0),
      });
      toast.success("Impact stats updated");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Invalid admin token");
        sessionStorage.removeItem("mise-admin-token");
        setAuthed(false);
      } else {
        toast.error("Could not save stats");
      }
    } finally {
      setSaving(false);
    }
  };

  const onChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className="mise-container py-20 md:py-24 max-w-2xl" data-testid="page-admin-impact">
      <p className="mise-eyebrow">Admin · Family Meals</p>
      <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight text-white">
        Impact stats
      </h1>
      <p className="mt-4 text-white/70 leading-relaxed">
        The counters on the Family Meals page now come straight from
        misefamilymeals.ca, updated every minute. The numbers below are a
        backup, shown only if misefamilymeals.ca can't be reached. Use the
        cumulative totals since launch.
      </p>

      {!authed ? (
        <form
          onSubmit={handleUnlock}
          className="mise-card p-8 mt-10"
          data-testid="admin-unlock-form"
        >
          <label className="text-sm text-white/60" htmlFor="admin-token">Admin token</label>
          <input
            id="admin-token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 mt-2 w-full"
            placeholder="Enter admin token"
            data-testid="admin-token-input"
            autoFocus
          />
          <button
            type="submit"
            className="mise-pill-primary mt-6"
            data-testid="admin-unlock-submit"
          >
            Unlock
          </button>
        </form>
      ) : (
        <form
          onSubmit={handleSave}
          className="mise-card p-8 mt-10 space-y-6"
          data-testid="admin-impact-form"
        >
          <div className="grid gap-2">
            <label className="text-sm text-white/60" htmlFor="kg-input">Kg rescued</label>
            <input
              id="kg-input"
              type="number"
              min="0"
              step="0.1"
              value={form.kg_rescued}
              onChange={onChange("kg_rescued")}
              className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30"
              data-testid="admin-kg-input"
              required
            />
            <p className="text-xs text-white/50">
              Preview: {formatKg(form.kg_rescued)} kg
            </p>
          </div>
          <div className="grid gap-2">
            <label className="text-sm text-white/60" htmlFor="meals-input">Meals enabled</label>
            <input
              id="meals-input"
              type="number"
              min="0"
              step="1"
              value={form.meals_enabled}
              onChange={onChange("meals_enabled")}
              className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30"
              data-testid="admin-meals-input"
              required
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm text-white/60" htmlFor="co2-input">CO₂e avoided (kg)</label>
            <input
              id="co2-input"
              type="number"
              min="0"
              step="0.1"
              value={form.co2e_avoided_kg}
              onChange={onChange("co2e_avoided_kg")}
              className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30"
              data-testid="admin-co2-input"
              required
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="mise-pill-primary"
              data-testid="admin-save-btn"
            >
              {saving ? "Saving…" : "Save impact stats"}
            </button>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem("mise-admin-token");
                setAuthed(false);
                setToken("");
              }}
              className="text-sm text-white/60 hover:text-white transition-colors"
              data-testid="admin-lock-btn"
            >
              Lock
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminImpact;
